import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import DictationInput from '../components/DictationInput';
import { toast } from 'sonner';
import { 
  GraduationCap, BookOpen, Clock, CheckCircle, ChevronRight, 
  Play, Award, Briefcase, Heart, DollarSign, MessageSquare
} from 'lucide-react';

const CATEGORY_ICONS = {
  workplace: Briefcase,
  life_skills: Heart,
  financial: DollarSign,
  communication: MessageSquare,
};

const CATEGORY_COLORS = {
  workplace: { bg: 'bg-sky-50', text: 'text-sky-700', accent: 'bg-sky-500' },
  life_skills: { bg: 'bg-emerald-50', text: 'text-emerald-700', accent: 'bg-emerald-500' },
  financial: { bg: 'bg-amber-50', text: 'text-amber-700', accent: 'bg-amber-500' },
  communication: { bg: 'bg-violet-50', text: 'text-violet-700', accent: 'bg-violet-500' },
};

export default function CoursesPage() {
  const { user, axiosAuth } = useAuth();
  const [coursesData, setCoursesData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [lessonPrompt, setLessonPrompt] = useState('');
  const [lessonResponse, setLessonResponse] = useState('');
  const [lessonResponseOriginal, setLessonResponseOriginal] = useState('');
  const [lessonEvidenceFile, setLessonEvidenceFile] = useState(null);
  const [lessonResponseData, setLessonResponseData] = useState(null);
  const [savingResponse, setSavingResponse] = useState(false);
  const [loading, setLoading] = useState(true);
  const api = axiosAuth();

  const load = async () => {
    try {
      const [coursesRes, summaryRes] = await Promise.all([
        api.get('/courses'),
        api.get('/courses/progress/summary')
      ]);
      setCoursesData(coursesRes.data);
      setSummary(summaryRes.data);
    } catch (e) {
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!currentLesson) return;
    setLessonPrompt('');
    setLessonResponse('');
    setLessonResponseOriginal('');
    setLessonEvidenceFile(null);
    setLessonResponseData(null);
    api.get(`/courses/lesson/${currentLesson.id}/prompt`).then(r => setLessonPrompt(r.data.prompt || '')).catch(() => {});
    api.get(`/courses/lesson/${currentLesson.id}/response`).then(r => {
      if (r.data?.response_text) {
        setLessonResponse(r.data.response_text);
        setLessonResponseOriginal(r.data.response_original || r.data.response_text);
        setLessonResponseData(r.data);
      }
    }).catch(() => {});
  }, [currentLesson]);

  const completeLesson = async (courseId, lessonId) => {
    if (!lessonResponse.trim()) {
      toast.error('Please answer the reflection question first');
      return;
    }
    setSavingResponse(true);
    try {
      let docIds = [];
      if (lessonEvidenceFile) {
        const formData = new FormData();
        formData.append('file', lessonEvidenceFile);
        formData.append('title', `${currentLesson?.title || 'Lesson'} Evidence`);
        formData.append('category', `skill_builder_${lessonId}`);
        const upload = await api.post('/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (upload?.data?.id) docIds = [upload.data.id];
      }
      await api.post(`/courses/lesson/${lessonId}/response`, {
        response_text: lessonResponse,
        response_original: lessonResponseOriginal || lessonResponse,
        evidence_doc_ids: docIds
      });
      const res = await api.post(`/courses/${courseId}/lesson/${lessonId}/complete`);
      toast.success(res.data.course_completed ? 'Course completed! 🎉' : 'Lesson completed!');
      load();
      if (selectedCourse) {
        const lessonIndex = selectedCourse.lessons.findIndex(l => l.id === lessonId);
        if (lessonIndex < selectedCourse.lessons.length - 1) {
          setCurrentLesson(selectedCourse.lessons[lessonIndex + 1]);
        } else {
          setCurrentLesson(null);
        }
      }
    } catch (e) {
      toast.error('Failed to complete lesson');
    } finally {
      setSavingResponse(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!coursesData) return null;

  const { courses, categories } = coursesData;

  // Group by category
  const coursesByCategory = courses.reduce((acc, course) => {
    const cat = course.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(course);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="courses-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Skill Builder</h1>
        <p className="text-muted-foreground text-sm mt-1">Short courses to build skills for work and life</p>
      </div>

      {/* Progress Summary */}
      {summary && (
        <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-teal-50 to-emerald-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-teal-600 flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-teal-700 font-medium">Your Progress</p>
                  <p className="text-2xl font-bold text-teal-900">{summary.completed_lessons} lessons</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-teal-600">{summary.overall_progress}%</p>
                <p className="text-xs text-teal-600/70">{summary.completed_courses} of {summary.total_courses} courses</p>
              </div>
            </div>
            <Progress value={summary.overall_progress} className="h-3 bg-teal-100" />
          </CardContent>
        </Card>
      )}

      {/* Courses by Category */}
      {Object.entries(coursesByCategory).map(([catKey, catCourses]) => {
        const catInfo = categories[catKey] || {};
        const Icon = CATEGORY_ICONS[catKey] || BookOpen;
        const colors = CATEGORY_COLORS[catKey] || CATEGORY_COLORS.workplace;

        return (
          <div key={catKey} className="space-y-3">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center`}>
                <Icon className={`w-4 h-4 ${colors.text}`} />
              </div>
              <h2 className="font-semibold">{catInfo.name || catKey}</h2>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {catCourses.map(course => (
                <Card 
                  key={course.id} 
                  className={`border-0 shadow-sm rounded-2xl hover:shadow-md transition-shadow cursor-pointer ${course.completed ? 'ring-2 ring-emerald-200' : ''}`}
                  onClick={() => setSelectedCourse(course)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className={`w-10 h-10 rounded-xl ${colors.bg} flex items-center justify-center`}>
                        {course.completed ? (
                          <CheckCircle className="w-5 h-5 text-emerald-600" />
                        ) : (
                          <Icon className={`w-5 h-5 ${colors.text}`} />
                        )}
                      </div>
                      {course.completed && (
                        <Badge className="bg-emerald-50 text-emerald-700">Complete</Badge>
                      )}
                    </div>
                    <h3 className="font-semibold text-sm">{course.title}</h3>
                    <p className="text-xs text-muted-foreground mt-1">{course.description}</p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock size={12} /> {course.duration}
                        <span>•</span>
                        <span>{course.total_lessons} lessons</span>
                      </div>
                      <span className={`text-xs font-medium ${course.completed ? 'text-emerald-600' : colors.text}`}>
                        {course.progress_percent}%
                      </span>
                    </div>
                    <Progress value={course.progress_percent} className="h-1.5 mt-2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
      })}

      {/* Course Detail Modal */}
      <Dialog open={!!selectedCourse && !currentLesson} onOpenChange={() => setSelectedCourse(null)}>
        <DialogContent className="rounded-2xl max-w-lg">
          {selectedCourse && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-3">
                  <div className={`w-12 h-12 rounded-xl ${CATEGORY_COLORS[selectedCourse.category]?.bg || 'bg-sky-50'} flex items-center justify-center`}>
                    {selectedCourse.completed ? (
                      <Award className="w-6 h-6 text-emerald-600" />
                    ) : (
                      <BookOpen className={`w-6 h-6 ${CATEGORY_COLORS[selectedCourse.category]?.text || 'text-sky-600'}`} />
                    )}
                  </div>
                  <div>
                    <DialogTitle>{selectedCourse.title}</DialogTitle>
                    <p className="text-sm text-muted-foreground">{selectedCourse.duration} • {selectedCourse.total_lessons} lessons</p>
                  </div>
                </div>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <p className="text-sm text-muted-foreground">{selectedCourse.description}</p>
                <div className="space-y-2">
                  {selectedCourse.lessons.map((lesson, i) => {
                    const isCompleted = selectedCourse.completed_lessons?.includes(lesson.id);
                    return (
                      <button
                        key={lesson.id}
                        onClick={() => setCurrentLesson(lesson)}
                        className={`w-full p-3 rounded-xl flex items-center gap-3 transition-all ${
                          isCompleted ? 'bg-emerald-50 border border-emerald-200' : 'bg-muted/30 hover:bg-muted/50'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          isCompleted ? 'bg-emerald-500 text-white' : 'bg-muted text-muted-foreground'
                        }`}>
                          {isCompleted ? <CheckCircle size={16} /> : i + 1}
                        </div>
                        <span className={`flex-1 text-left text-sm ${isCompleted ? 'text-emerald-800' : ''}`}>
                          {lesson.title}
                        </span>
                        <ChevronRight size={16} className="text-muted-foreground" />
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Lesson View Modal */}
      <Dialog open={!!currentLesson} onOpenChange={() => setCurrentLesson(null)}>
        <DialogContent className="rounded-2xl max-w-lg">
          {currentLesson && selectedCourse && (
            <>
              <DialogHeader>
                <DialogTitle>{currentLesson.title}</DialogTitle>
                <p className="text-sm text-muted-foreground">{selectedCourse.title}</p>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="p-4 rounded-xl bg-muted/30">
                  <p className="text-sm leading-relaxed">{currentLesson.content}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-semibold">Reflection Prompt</p>
                  <p className="text-sm text-muted-foreground" data-testid="lesson-prompt">
                    {lessonPrompt || 'Loading prompt...'}
                  </p>
                  <DictationInput
                    value={lessonResponse}
                    onChange={(cleaned, original) => {
                      setLessonResponse(cleaned);
                      setLessonResponseOriginal(original || cleaned);
                    }}
                    rows={3}
                    placeholder="Share your response..."
                    data-testid="lesson-response-input"
                  />
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Upload proof or example (optional)</p>
                    <Input type="file" onChange={e => setLessonEvidenceFile(e.target.files?.[0] || null)} data-testid="lesson-evidence-file" />
                  </div>
                  {lessonResponseData?.response_text && (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3" data-testid="lesson-response-saved">
                      <p className="text-xs uppercase text-emerald-700 mb-1">Saved Response</p>
                      <p className="text-sm text-emerald-800">{lessonResponseData.response_text}</p>
                    </div>
                  )}
                </div>
                {!selectedCourse.completed_lessons?.includes(currentLesson.id) ? (
                  <Button 
                    onClick={() => completeLesson(selectedCourse.id, currentLesson.id)}
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2"
                    disabled={savingResponse}
                    data-testid="lesson-complete-button"
                  >
                    <CheckCircle size={16} /> Submit & Mark Complete
                  </Button>
                ) : (
                  <div className="text-center text-sm text-emerald-600 font-medium flex items-center justify-center gap-2">
                    <CheckCircle size={16} /> Lesson Completed
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Encouragement */}
      <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50">
        <CardContent className="p-6 text-center">
          <GraduationCap className="w-8 h-8 text-amber-500 mx-auto mb-2" />
          <p className="text-amber-800 font-medium">
            {summary?.overall_progress === 100 
              ? "You've completed all courses! You're ready to succeed!"
              : summary?.overall_progress >= 50
                ? "Great progress! Keep learning and growing!"
                : "Every lesson brings you closer to your goals!"}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
